import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpParamsOptions, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { retry, catchError, map, tap } from 'rxjs/operators';

import { AppConfigService } from 'app/core/config/app-config.service';

import { SortDirection } from '@angular/material/sort';

/*
const HttpUploadOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Headers': '*',
  }),
};
*/

// Enrolled course info (from eager loading)
export interface EnrolledCourse {
  student_course_id: number;
  course_id: number;
  course_name: string;
  course_image?: string;
  course_status?: number;
  status_id: number;
  startdate?: string;
  enddate?: string;
  completion?: string;
  points?: number;
}

export interface Student {
  t_ot_studentid?: number; // Primary key, optional if not always present
  name?: string;
  email?: string;
  info?: string;
  referal?: string;
  department?: string;
  picture?: string;
  post?: string;
  info1?: string;
  info2?: string;
  info4?: string;
  phone2?: string;
  reminder?: number;
  last_course_start?: Date;
  last_course_title?: string;
  last_course_status2?: string;
  lang?: string;
  info3?: string;
  last_login?: string; // Assuming this is a string; if it's a date, convert it to Date type
  info5?: string;
  mobile?: string;
  created?: Date;
  createdby?: number;
  modified?: Date;
  modifiedby?: number;
  company_id?: number;
  skype_id?: string;
  advisor_id?: number;
  registration_date?: Date;
  street?: string;
  phone?: string;
  company?: string;
  zip?: string;
  city?: string;
  country?: string;
  username?: string;
  account_id?: number;
  beusergroup_id?: number;
  div_id?: number;
  status_id?: number;
  mandatory_steps?: number;
  twin_id?: number;
  uuid?: string;
  // Enrolled courses (when include_courses=true)
  enrolled_courses?: EnrolledCourse[];
}

export interface StudentSingle {
  data: Student;
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface StudentTable {
  data: Student[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    // 'Access-Control-Allow-Headers': '*',
    'Access-Control-Allow-Origin': '*',
  }),
};

//
// --------------------------------------------------------------
//

@Injectable({
  providedIn: 'root',
})
export class StudentService {
  errorMsg: string = '';

  constructor(private httpClient: HttpClient, readonly appConfig: AppConfigService) {}

  /**
   * GET ALL
   *
   * @return response()
   */

  public getStudents(
    pageNumber: Number = 1,
    pageSize: Number = 20,
    sort: string = '',
    order: SortDirection = '',
    searchTerm: string = '',
    filtersArgs: string = '',
    companyId: number | null = null,
    includeCourses: boolean = false
  ): Observable<StudentTable> {
    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/students`;
    // '?page=${pageNumber}&per_page=${pageSize}&sort=${sort}&sorder=${order}`

    let params = new HttpParams()
      .set('page', '' + pageNumber)
      .set('per_page', '' + pageSize)
      .set('sort', sort)
      .set('sorder', order)
      .set('sterm', searchTerm)
      .set('filters', filtersArgs);
    
    // Add company_id filter if specified
    // When null, API will use all allowed companies from JWT token
    if (companyId !== null) {
      params = params.set('company_id', '' + companyId);
    }
    
    // Include enrolled courses in response (avoids N+1 queries)
    if (includeCourses) {
      params = params.set('include_courses', 'true');
    }

    return this.httpClient.get<any>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        // 'Access-Control-Allow-Headers': '*',
        'Access-Control-Allow-Origin': '*',
      }),
      params: params,
    }).pipe(
      map((response: any) => {
        // Handle API response format: {status, count, data, sec}
        if (response.status === 'OK' && response.data) {
          return {
            data: response.data,
            page: pageNumber as number,
            per_page: pageSize as number,
            total: response.count || response.data.length,
            total_pages: Math.ceil((response.count || response.data.length) / (pageSize as number))
          } as StudentTable;
        }
        // Handle standard StudentTable format
        if (response.data && Array.isArray(response.data)) {
          return response as StudentTable;
        }
        // Fallback: assume response is already in correct format
        return response as StudentTable;
      }),
      catchError((error) => {
        console.error('Error fetching students:', error);
        return throwError(() => error);
      })
    );

    /*
    .pipe(
      map((response: StudentTable) => {
        //debugger;
        return response.data.map((item: Student) => ({
          ...item,
        }));
      }),
      tap((response: any) => {
        console.log('mrio');
        console.log(response);
        return <Student[]>response;
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          errorMsg = `Error: ${error.error.message}`;
        } else {
          errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
    */
  }

  /**
   * GET / FIND BY ID
   *
   * @return response()
   */
  getStudentById(id: number): Observable<StudentSingle> {
    // Validate id - don't make API call for invalid IDs (0, negative, or non-numeric)
    if (!id || id <= 0 || isNaN(id)) {
      console.warn('StudentService.getStudentById: Invalid student ID:', id);
      // Return an empty response to prevent API error
      return of({
        data: null,
        page: 0,
        per_page: 0,
        total: 0,
        total_pages: 0
      } as StudentSingle);
    }

    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/students/` + id;

    return this.httpClient
      .get<StudentSingle>(apiUrl, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          // 'Access-Control-Allow-Headers': '*',
          'Access-Control-Allow-Origin': '*',
        }),
        params: new HttpParams().set('id', '' + id),
      })
      .pipe(
        map((response: StudentSingle) => {
          return {
            ...response,
          };
        })
      );

    /*
    return this.httpClient.get<Student[]>(this.appConfig.get('serverUrl') + '/Student/' + id, httpOptions).pipe(
      tap((resp) => {
        //this.repo.setQuizes( resp || []);
        return resp;
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
    */
  }

  /**
   * GET / FIND BY FILTER
   *
   * @return response()
   */
  getStudentByParams(data: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    //const data: any = { this: 'thisThing', that: 'thatThing', other: 'otherThing'};

    const httpParams: HttpParamsOptions = { fromObject: data } as HttpParamsOptions;

    const options = { params: new HttpParams(httpParams), headers: headers };

    return this.httpClient.get<Student[]>(this.appConfig.get('serverUrl') + '/v1/students/', options).pipe(
      tap((resp) => {
        //this.repo.setQuizes( resp || []);
        return resp;
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  /**
   * CREATE
   *
   * @return response()
   */
  createStudent(post: any): Observable<any> {
    return this.httpClient.post<any>(this.appConfig.get('serverUrl') + '/v1/student/', JSON.stringify(post), httpOptions).pipe(
      map((response) => {
        return {
          ...response.data,
          uuid: response.data.uuid,
          student: '',
        };

        /*
          response.data.map((item) => ({
            ...item,
            id: item.quuid,
            quizdata: ''
          }));

          return response;
          */
      }),
      tap((response: any) => {
        console.log(response);
        //this.repo.addQuiz(response);
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  /**
   * UPDATE
   *
   * @return response()
   */
  updateStudent(student: Student): Observable<any> {
    let id = student.t_ot_studentid;
    const data = {
      //'id': quiz.qdata.id,
      uuid: '' + student.uuid,
    };

    // do the update
    /*     return this.httpClient.post(this.appConfig.get('serverUrl') + '/v5/student/' + id, data, httpOptions)
      .pipe(
        catchError((res: HttpErrorResponse) => {
            console.log(res);
            return throwError(JSON.stringify(res));

        })
      );*/

    return this.httpClient.post<any>(this.appConfig.get('serverUrl') + '/v1/student/' + id, JSON.stringify(data), httpOptions).pipe(
      map((response) => {
        console.log('map');
        return {
          ...response.data,
          id: response.data.uuid,
          student: '',
        };
      }),
      tap((response: any) => {
        console.log('tAP');
        //this.repo.updateQuiz(response);
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  /**
   * DELETE
   *
   * @return response()
   */
  deleteStudent(id: string) {
    return this.httpClient.delete(this.appConfig.get('serverUrl') + '/v1/student/' + id, httpOptions).pipe(
      tap((value) => {
        //this.repo.removeQuiz(id);
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  /**
   * Write code on Method
   *
   * @return response()
   */
  private getServerErrorMessage(error: HttpErrorResponse) {
    console.log('custom service error handler');

    switch (error.status) {
      case 0: {
        // A client-side or network error occurred. Handle it accordingly.
        //console.error('An error occurred:', error.error);
        return `An error occurred: ${error.error}`;
      }
      case 404: {
        return `Not Found: ${error.message}`;
      }
      case 403: {
        return `Access Denied: ${error.message}`;
      }
      case 500: {
        return `Internal Server Error: ${error.message}`;
      }
      default: {
        return `Unknown Server Error: ${error.error}`;
      }

      // Return an observable with a user-facing error message.
      //return throwError(() => new Error('Something bad happened; please try again later.'));
    }
  }
}
